/**
 * Storefront ACL – Home Mappers (Slice 13).
 *
 * `GET /home` devuelve SIEMPRE `sections`: las del editor del dueño o, si la
 * tienda no configuró su inicio, las por defecto. Las dos llegan con la misma
 * forma (los productos ya proyectados por la API en `section.products`, precio
 * en centavos). Este mapper es el único punto de entrada al shape que consume
 * HomePage: normaliza tipos y descarta lo que no se puede renderizar.
 */

import type {
  HomeProductSummary,
  HomeSection,
  HomeSectionType,
  PresentationUnit,
} from "@/features/home/types";

const SECTION_TYPES: ReadonlySet<HomeSectionType> = new Set([
  "HERO",
  "PRODUCT_GRID",
  "PRODUCT_STRIP",
  "CATEGORY_STRIP",
  "BANNER",
  "TEXT_BLOCK",
]);

const PRESENTATION_UNITS: ReadonlySet<PresentationUnit> = new Set([
  "UNIT",
  "G",
  "KG",
  "ML",
  "L",
]);

type Raw = Record<string, unknown>;

function isRecord(value: unknown): value is Raw {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  const n = typeof value === "number" ? value : Number.NaN;
  return Number.isFinite(n) ? n : null;
}

/** Producto de una sección (forma PublicHomeProductSummary de la API). */
export function mapHomeProduct(raw: unknown): HomeProductSummary | null {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  const slug = str(raw.slug);
  const name = str(raw.name);
  if (!id || !slug || !name) return null;

  const unit = raw.presentationUnit;
  return {
    id,
    slug,
    name,
    price: Math.round(num(raw.price) ?? 0),
    currency: str(raw.currency) ?? "CUP",
    imageUrl: str(raw.imageUrl),
    categoryName: str(raw.categoryName),
    presentationValue: num(raw.presentationValue),
    presentationUnit:
      typeof unit === "string" && PRESENTATION_UNITS.has(unit as PresentationUnit)
        ? (unit as PresentationUnit)
        : null,
  };
}

/** Sección del inicio. Tipo desconocido o sin id → null (no se renderiza). */
export function mapHomeSection(raw: unknown): HomeSection | null {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  const type = raw.type;
  if (!id || typeof type !== "string" || !SECTION_TYPES.has(type as HomeSectionType)) {
    return null;
  }

  const section: HomeSection = {
    id,
    slug: str(raw.slug) ?? id,
    type: type as HomeSectionType,
    title: str(raw.title),
    subtitle: str(raw.subtitle),
    config: raw.config ?? null,
    layout: raw.layout ?? null,
    active: raw.active !== false,
    position: num(raw.position) ?? 0,
  };

  if (type === "PRODUCT_GRID" || type === "PRODUCT_STRIP") {
    section.products = (Array.isArray(raw.products) ? raw.products : [])
      .map(mapHomeProduct)
      .filter((p): p is HomeProductSummary => p !== null);
  }

  return section;
}

/** Payload de `GET /home` (con o sin envelope) → secciones listas para HomePage. */
export function mapHomeSections(payload: unknown): HomeSection[] {
  const data = isRecord(payload) && isRecord(payload.data) ? payload.data : payload;
  const raw = isRecord(data) && Array.isArray(data.sections) ? data.sections : [];
  return raw
    .map(mapHomeSection)
    .filter((s): s is HomeSection => s !== null)
    .sort((a, b) => a.position - b.position);
}
